import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useDateStore } from "../stores/date-store";
import { apiGet } from "../lib/api";
import type { ReactNode } from "react";
import { formatDateFr } from "../lib/format";

// Onglet Indicateurs : soldes intermédiaires de gestion, courtage, structure
// financière et trésorerie, avec comparatif N-1. Mêmes calculs que l'export
// PDF banque (api/src/lib/financial-summary.ts) : la page ne fait qu'afficher.

export const Route = createFileRoute("/indicators")({
  component: IndicatorsPage,
});

type MetricFormat = "eur" | "percent" | "ratio";

interface Metric {
  key: string;
  label: string;
  value: number | null;
  valueN1: number | null;
  format: MetricFormat;
  emphasis?: boolean;
  note?: string;
  formula: string;
}

interface ProductMetrics {
  key: string;
  name: string;
  ca: number;
  primes: number;
  marge: number;
  caN1: number | null;
  primesN1: number | null;
  margeN1: number | null;
}

interface SummaryData {
  period: { startDate: string; endDate: string };
  pnlN1Period: { startDate: string; endDate: string } | null;
  balanceAsOf: string;
  balanceN1AsOf: string | null;
  summary: {
    hasN1: boolean;
    sig: Metric[];
    structure: Metric[];
    tresorerie: Metric[];
    courtage: { global: Metric[]; byProduct: ProductMetrics[] };
  };
  warnings: string[];
}

// ---------------------------------------------------------------------------
// Formats
// ---------------------------------------------------------------------------

const PERCENT = new Intl.NumberFormat("fr-FR", { style: "percent", maximumFractionDigits: 1 });
const RATIO = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const EUR = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

// Même règle d'arrondi que le PDF (api/src/lib/pdf/format.ts) : montants
// arrondis à l'euro sans « -0 », variation = différence des montants arrondis.
function roundEur(n: number): number {
  const r = Math.round(n);
  return Object.is(r, -0) ? 0 : r;
}

// Pourcentages (dixième de point) et ratios (centième) : une seule
// quantification, utilisée pour l'affichage ET la variation, afin que
// « 1,01 − 1,00 » donne bien +0,01. Elle passe par Intl.NumberFormat lui-même
// (représentation décimale, demi vers l'extérieur) : une multiplication
// flottante (1,005 × 100 = 100,4999…) donnerait un résultat différent de
// celui que l'utilisateur lit.
const EN_PERCENT = new Intl.NumberFormat("en-US", { style: "percent", maximumFractionDigits: 1, useGrouping: false });
const EN_RATIO = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2, useGrouping: false });
function noNegZero(n: number): number {
  return Object.is(n, -0) ? 0 : n;
}
function quantizePercent(n: number): number {
  return noNegZero(Number(EN_PERCENT.format(n).replace("%", "")) / 100);
}
function quantizeRatio(n: number): number {
  return noNegZero(Number(EN_RATIO.format(n)));
}

// `n1Available` distingue l'absence de comparatif (cellule vide) d'un ratio
// indéfini sur N-1 (« n/a »), comme dans le PDF.
function fmtValue(v: number | null, format: MetricFormat): string {
  if (v == null || !Number.isFinite(v)) return "n/a";
  if (format === "eur") return EUR.format(roundEur(v));
  if (format === "percent") return PERCENT.format(quantizePercent(v));
  return RATIO.format(quantizeRatio(v));
}

function fmtN1(v: number | null, format: MetricFormat, n1Available: boolean): string {
  return n1Available ? fmtValue(v, format) : "";
}

// Variation N − N-1 : différence des euros arrondis pour les montants, en
// points (au dixième) pour les pourcentages, en écart brut (au centième) pour
// les ratios. Vide sans N-1 ou si l'une des valeurs est indéfinie.
function fmtDelta(v: number | null, v1: number | null, format: MetricFormat): { text: string; tone: "up" | "down" | "flat" } {
  if (v == null || v1 == null || !Number.isFinite(v) || !Number.isFinite(v1)) return { text: "", tone: "flat" };
  let d: number;
  let text: string;
  if (format === "eur") {
    d = roundEur(v) - roundEur(v1);
    text = d === 0 ? "0 €" : `${d > 0 ? "+" : "−"}${EUR.format(Math.abs(d))}`;
  } else if (format === "percent") {
    d = quantizePercent(quantizePercent(v) - quantizePercent(v1));
    text = d === 0 ? "0 pt" : `${d > 0 ? "+" : "−"}${(Math.abs(d) * 100).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} pt`;
  } else {
    d = quantizeRatio(quantizeRatio(v) - quantizeRatio(v1));
    text = d === 0 ? "0" : `${d > 0 ? "+" : "−"}${RATIO.format(Math.abs(d))}`;
  }
  return { text, tone: d > 0 ? "up" : d < 0 ? "down" : "flat" };
}

const TONE_CLASS = { up: "text-green-700", down: "text-red-700", flat: "text-gray-500" } as const;

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function IndicatorsPage() {
  const { startDate, endDate } = useDateStore();
  const { data, isPending, isFetching, error } = useQuery<SummaryData>({
    queryKey: ["financial-summary", startDate, endDate],
    queryFn: () => apiGet<SummaryData>(`/api/reports/summary?${new URLSearchParams({ startDate, endDate })}`),
  });

  if (isPending) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Chargement...</div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">Erreur de chargement{error ? ` : ${(error as Error).message}` : ""}</div>
      </div>
    );
  }

  const { summary } = data;
  const yearN = endDate.slice(0, 4);
  const pnlN1Label = data.pnlN1Period ? `${formatDateFr(data.pnlN1Period.startDate)} → ${formatDateFr(data.pnlN1Period.endDate)}` : null;
  const balanceN1Label = data.balanceN1AsOf ? `au ${formatDateFr(data.balanceN1AsOf)}` : null;
  const find = (list: Metric[], key: string) => list.find((m) => m.key === key);
  const tiles: (Metric | undefined)[] = [
    find(summary.sig, "ca"),
    find(summary.courtage.global, "marge"),
    find(summary.courtage.global, "taux"),
    find(summary.sig, "rn"),
    find(summary.tresorerie, "treso"),
    find(summary.structure, "cp"),
  ];

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Indicateurs</h1>
        <span className="text-sm text-gray-500">
          Du {formatDateFr(startDate)} au {formatDateFr(endDate)} · bilan au {formatDateFr(data.balanceAsOf)}
          {isFetching && " · actualisation…"}
        </span>
      </div>

      {data.warnings.length > 0 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded p-4 text-sm text-yellow-900">
          <div className="font-semibold mb-1">Points d'attention</div>
          <ul className="list-disc pl-5 space-y-0.5">
            {data.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {tiles.map((m) => m && <Tile key={m.key} metric={m} n1Available={m.format === "eur" && ["treso", "cp"].includes(m.key) ? data.balanceN1AsOf !== null : data.pnlN1Period !== null} />)}
      </div>

      <p className="text-xs text-gray-500">
        Mêmes calculs que l'export PDF « bilan + P&L ». Comparatif N-1 : résultat sur la même période décalée d'un an
        {pnlN1Label ? ` (${pnlN1Label})` : " (aucune écriture)"}, bilan au dernier 31 décembre{balanceN1Label ? ` (${balanceN1Label})` : " (aucun)"}. Cliquez un libellé (ou une tuile) pour afficher sa formule.
      </p>

      <MetricTable title="Soldes intermédiaires de gestion" metrics={summary.sig} yearN={yearN} n1Label={pnlN1Label} n1Available={data.pnlN1Period !== null} />
      <MetricTable title="Activité de courtage" metrics={summary.courtage.global} yearN={yearN} n1Label={pnlN1Label} n1Available={data.pnlN1Period !== null}>
        <ProductTable rows={summary.courtage.byProduct} hasN1={data.pnlN1Period !== null} yearN={yearN} />
      </MetricTable>
      <MetricTable title="Structure financière" metrics={summary.structure} yearN={yearN} n1Label={balanceN1Label} n1Available={data.balanceN1AsOf !== null} />
      <MetricTable title="Trésorerie" metrics={summary.tresorerie} yearN={yearN} n1Label={balanceN1Label} n1Available={data.balanceN1AsOf !== null} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Composants
// ---------------------------------------------------------------------------

function Tile({ metric, n1Available }: { metric: Metric; n1Available: boolean }) {
  const [open, setOpen] = useState(false);
  const delta = fmtDelta(metric.value, metric.valueN1, metric.format);
  const id = `tile-formula-${metric.key}`;
  return (
    <button
      type="button"
      onClick={() => setOpen((o) => !o)}
      aria-expanded={open}
      aria-controls={id}
      title={metric.formula}
      className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
    >
      <div className="text-sm text-gray-500">{metric.label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">{fmtValue(metric.value, metric.format)}</div>
      <div className="text-xs text-gray-500 tabular-nums">
        {n1Available ? `N-1 : ${fmtValue(metric.valueN1, metric.format)}` : "N-1 : aucun comparatif"}
        {delta.text && <span className={`ml-2 ${TONE_CLASS[delta.tone]}`}>{delta.text}</span>}
      </div>
      <div id={id} className={`mt-2 text-xs text-gray-600 ${open ? "" : "hidden"}`}>
        {metric.formula}
      </div>
    </button>
  );
}

function MetricTable({ title, metrics, yearN, n1Label, n1Available, children }: { title: string; metrics: Metric[]; yearN: string; n1Label: string | null; n1Available: boolean; children?: ReactNode }) {
  const [openKeys, setOpenKeys] = useState<Set<string>>(() => new Set());
  const toggle = (key: string) =>
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
      </div>
      <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead className="border-b border-gray-200">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Indicateur</th>
            <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase w-36">N ({yearN})</th>
            <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase w-36" title={n1Label ?? undefined}>
              N-1
            </th>
            <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase w-32">Var.</th>
          </tr>
        </thead>
        <tbody>
          {metrics.map((m, i) => {
            const delta = fmtDelta(m.value, m.valueN1, m.format);
            const open = openKeys.has(m.key);
            const id = `formula-${m.key}`;
            return (
              <tr key={m.key} className={`${i % 2 === 1 ? "bg-gray-50" : ""} ${m.emphasis ? "font-semibold" : ""} align-top`}>
                <td className="px-4 py-1.5 text-sm text-gray-900">
                  <button
                    type="button"
                    onClick={() => toggle(m.key)}
                    aria-expanded={open}
                    aria-controls={id}
                    title={m.formula}
                    className="border-b border-dotted border-gray-400 text-left hover:border-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                  >
                    {m.label}
                  </button>
                  {m.note && <span className="ml-2 text-xs font-normal text-gray-400">({m.note})</span>}
                  <div id={id} className={`mt-1 text-xs font-normal text-gray-600 ${open ? "" : "hidden"}`}>
                    {m.formula}
                  </div>
                </td>
                <td className="px-4 py-1.5 text-sm text-right tabular-nums text-gray-900">{fmtValue(m.value, m.format)}</td>
                <td className="px-4 py-1.5 text-sm text-right tabular-nums text-gray-500">{fmtN1(m.valueN1, m.format, n1Available)}</td>
                <td className={`px-4 py-1.5 text-sm text-right tabular-nums ${TONE_CLASS[delta.tone]}`}>{delta.text}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      {children}
    </div>
  );
}

function ProductTable({ rows, hasN1, yearN }: { rows: ProductMetrics[]; hasN1: boolean; yearN: string }) {
  if (rows.length === 0) return null;
  const cell = "px-4 py-1.5 text-sm text-right tabular-nums";
  const rate = (marge: number, ca: number) => (Math.abs(ca) < 0.005 ? "n/a" : PERCENT.format(marge / ca));
  const eur = (n: number) => EUR.format(roundEur(n));
  return (
    <div className="border-t border-gray-200">
      <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Par produit ({yearN}{hasN1 ? " · N-1 entre parenthèses" : ""})</div>
      <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead className="border-b border-gray-200">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Produit</th>
            <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">CA prix public</th>
            <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Primes</th>
            <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Marge</th>
            <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Taux</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.key} className={i % 2 === 1 ? "bg-gray-50" : ""}>
              <td className="px-4 py-1.5 text-sm text-gray-900">{r.name}</td>
              <td className={`${cell} text-gray-900`}>
                {eur(r.ca)}
                {hasN1 && r.caN1 != null && <span className="ml-1 text-xs text-gray-500">({eur(r.caN1)})</span>}
              </td>
              <td className={`${cell} text-gray-900`}>
                {eur(r.primes)}
                {hasN1 && r.primesN1 != null && <span className="ml-1 text-xs text-gray-500">({eur(r.primesN1)})</span>}
              </td>
              <td className={`${cell} font-semibold text-gray-900`}>
                {eur(r.marge)}
                {hasN1 && r.margeN1 != null && <span className="ml-1 text-xs font-normal text-gray-500">({eur(r.margeN1)})</span>}
              </td>
              <td className={`${cell} text-gray-900`}>
                {rate(r.marge, r.ca)}
                {hasN1 && r.margeN1 != null && r.caN1 != null && <span className="ml-1 text-xs text-gray-500">({rate(r.margeN1, r.caN1)})</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <p className="px-4 py-2 text-xs text-gray-500">
        Avant 2026 les primes étaient comptabilisées par assureur à l'encaissement : la ventilation par produit de l'exercice précédent est indicative, le total reste exact.
      </p>
    </div>
  );
}
